// src/pages/ClientManagementPage.js
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { formatRut, formatPhone, formatCurrency } from '../utils/formatters';
import { clientService } from '../services/clientService'; // Importamos el servicio
import { 
    PhoneIcon, EnvelopeIcon, EyeIcon, PencilSquareIcon, TrashIcon, 
    DocumentArrowDownIcon, DocumentArrowUpIcon, UserGroupIcon, ChatBubbleBottomCenterTextIcon, 
    UserIcon, CurrencyDollarIcon, BuildingOfficeIcon, WrenchIcon, KeyIcon, 
    BriefcaseIcon, ExclamationTriangleIcon
} from '@heroicons/react/24/solid';

// --- MODAL DE DETALLES (Lectura) ---
const ClientDetailsModal = ({ client, onClose }) => {
    const [activeTab, setActiveTab] = useState('info');
    const [historyData, setHistoryData] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);

    useEffect(() => {
        if (activeTab === 'info') return;

        const fetchHistory = async () => {
            setLoadingHistory(true);
            try {
                // Usamos el servicio para obtener historial
                const data = await clientService.getHistory(client.id, activeTab);
                setHistoryData(data);
            } catch (error) {
                console.error("Error fetching history:", error);
            } finally {
                setLoadingHistory(false);
            }
        };

        fetchHistory();
    }, [client, activeTab]);

    const formatDate = (timestamp) => {
        if (!timestamp) return '-';
        return new Date(timestamp.seconds * 1000).toLocaleDateString('es-CL');
    };

    // Tarjeta para mostrar contacto en modo lectura
    const ContactCardDisplay = ({ title, icon: Icon, name, phone, email, color = "bg-white dark:bg-gray-800" }) => {
        if (!name && !phone && !email) return null;
        return (
            <div className={`p-4 rounded-lg border dark:border-gray-700 shadow-sm transition-colors ${color}`}>
                <h4 className="font-bold text-gray-800 dark:text-gray-200 flex items-center mb-2 text-sm uppercase tracking-wide">
                    <Icon className="w-4 h-4 mr-2 text-gray-500 dark:text-gray-400"/> {title}
                </h4>
                <div className="text-sm space-y-1.5 pl-1">
                    {name && <p className="font-semibold text-gray-900 dark:text-gray-100">{name}</p>}
                    {phone && (
                        <a href={`tel:${phone}`} className="text-blue-600 dark:text-blue-400 hover:underline flex items-center">
                            <PhoneIcon className="w-3 h-3 mr-1.5 text-gray-400 dark:text-gray-500"/> {formatPhone(phone)}
                        </a>
                    )}
                    {email && (
                        <a href={`mailto:${email}`} className="text-blue-600 dark:text-blue-400 hover:underline flex items-center">
                            <EnvelopeIcon className="w-3 h-3 mr-1.5 text-gray-400 dark:text-gray-500"/> {email}
                        </a>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
            <div className="bg-white dark:bg-gray-900 rounded-xl w-full max-w-5xl h-[90vh] flex flex-col shadow-2xl overflow-hidden animate-fade-in border border-transparent dark:border-gray-700 transition-colors">
                {/* Header Azul */}
                <div className="p-6 border-b dark:border-gray-700 flex justify-between items-start bg-blue-900 dark:bg-blue-950 text-white transition-colors">
                    <div>
                        <h2 className="text-2xl font-bold">{client.nombre}</h2>
                        <p className="text-blue-200 text-sm mt-1 flex items-center">
                            <BriefcaseIcon className="w-4 h-4 mr-1"/> RUT: {client.rut === 'PENDIENTE' ? 'Ficha Incompleta' : formatRut(client.id)}
                        </p>
                    </div>
                    <button onClick={onClose} className="text-blue-200 hover:text-white text-3xl font-bold leading-none">&times;</button>
                </div>

                {/* Tabs */}
                <div className="flex border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-800 transition-colors">
                    <button onClick={() => setActiveTab('info')} className={`flex-1 py-4 text-center font-semibold text-sm transition-colors ${activeTab === 'info' ? 'border-b-4 border-blue-900 dark:border-blue-500 text-blue-900 dark:text-blue-400 bg-white dark:bg-gray-900' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>
                        INFORMACIÓN DE CONTACTO
                    </button>
                    <button onClick={() => setActiveTab('visits')} className={`flex-1 py-4 text-center font-semibold text-sm transition-colors ${activeTab === 'visits' ? 'border-b-4 border-blue-900 dark:border-blue-500 text-blue-900 dark:text-blue-400 bg-white dark:bg-gray-900' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>
                        HISTORIAL DE VISITAS
                    </button>
                    <button onClick={() => setActiveTab('quotes')} className={`flex-1 py-4 text-center font-semibold text-sm transition-colors ${activeTab === 'quotes' ? 'border-b-4 border-blue-900 dark:border-blue-500 text-blue-900 dark:text-blue-400 bg-white dark:bg-gray-900' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>
                        HISTORIAL DE COTIZACIONES
                    </button>
                </div>

                {/* Contenido */}
                <div className="flex-1 overflow-y-auto p-6 bg-gray-50 dark:bg-gray-900 transition-colors">
                    {activeTab === 'info' ? (
                        <div className="space-y-6">
                            {/* Datos Generales */}
                            <div className="bg-white dark:bg-gray-800 p-5 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm transition-colors">
                                <h3 className="text-base font-bold text-gray-800 dark:text-gray-200 mb-4 border-b dark:border-gray-700 pb-2">DATOS COMERCIALES</h3>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    <div><p className="text-xs text-gray-400 dark:text-gray-500 uppercase font-semibold">Dirección</p><p className="text-gray-900 dark:text-gray-100">{client.direccion || '-'}</p></div>
                                    <div><p className="text-xs text-gray-400 dark:text-gray-500 uppercase font-semibold">Teléfono Central</p><p className="text-gray-900 dark:text-gray-100">{formatPhone(client.telefono) || '-'}</p></div>
                                    <div><p className="text-xs text-gray-400 dark:text-gray-500 uppercase font-semibold">Email Central</p><p className="text-gray-900 dark:text-gray-100">{client.email || '-'}</p></div>
                                </div>
                                {client.notes && (
                                    <div className="mt-4 bg-yellow-50 dark:bg-yellow-900/30 p-3 rounded border border-yellow-200 dark:border-yellow-800 text-sm text-gray-700 dark:text-gray-300 flex items-start transition-colors">
                                        <ChatBubbleBottomCenterTextIcon className="w-5 h-5 mr-2 text-yellow-600 dark:text-yellow-500 shrink-0 mt-0.5"/>
                                        <div><strong>Notas:</strong> {client.notes}</div>
                                    </div>
                                )}
                            </div>

                            {/* Grid de Contactos */}
                            <h3 className="text-base font-bold text-gray-800 dark:text-gray-200 border-b dark:border-gray-700 pb-2 mt-6 mb-4">EQUIPO DE CONTACTO</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                <ContactCardDisplay title="Contacto Principal" icon={UserIcon} name={client.mainName} phone={client.mainPhone} email={client.mainEmail} color="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800/50" />
                                <ContactCardDisplay title="Encargado de Pagos" icon={CurrencyDollarIcon} name={client.paymentName} phone={client.paymentPhone} email={client.paymentEmail} />
                                <ContactCardDisplay title="Administrador" icon={BuildingOfficeIcon} name={client.adminName} phone={client.adminPhone} email={client.adminEmail} />
                                <ContactCardDisplay title="Mayordomo" icon={KeyIcon} name={client.conciergeName} phone={client.conciergePhone} email={client.conciergeEmail} />
                                <ContactCardDisplay title="Operaciones" icon={WrenchIcon} name={client.opsName} phone={client.opsPhone} email={client.opsEmail} />
                            </div>
                        </div>
                    ) : (
                        // Tabla de Historial
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 overflow-hidden transition-colors">
                            {loadingHistory ? (
                                <div className="text-center py-12 text-gray-500 dark:text-gray-400">Cargando historial...</div>
                            ) : historyData.length === 0 ? (
                                <div className="text-center py-12 text-gray-400 dark:text-gray-500 italic">No se encontraron registros.</div>
                            ) : (
                                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                    <thead className="bg-gray-100 dark:bg-gray-700/50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase">Folio</th>
                                            <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase">Fecha</th>
                                            <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase">{activeTab === 'visits' ? 'Técnico / Tipo' : 'Monto Total'}</th>
                                            <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase">Estado / Detalle</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                                        {historyData.map(item => (
                                            <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                                <td className="px-6 py-4 text-sm font-medium text-blue-600 dark:text-blue-400">{item.id}</td>
                                                <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">
                                                    {activeTab === 'visits' 
                                                        ? (item.fecha ? new Date(item.fecha.seconds * 1000).toLocaleDateString('es-CL') : '-')
                                                        : formatDate(item.createdAt)
                                                    }
                                                </td>
                                                <td className="px-6 py-4 text-sm text-gray-800 dark:text-gray-200 font-medium">
                                                    {activeTab === 'visits' 
                                                        ? `${item.tecnico || '-'} (${item.tipoVisita || 'Gral'})`
                                                        : formatCurrency(item.total || 0)
                                                    }
                                                </td>
                                                <td className="px-6 py-4 text-sm">
                                                    {activeTab === 'visits' ? (
                                                        <span className="truncate max-w-xs block text-gray-500 dark:text-gray-400">{item.diagnostico || 'Sin diagnóstico'}</span>
                                                    ) : (
                                                        <span className={`px-2 py-0.5 inline-flex text-xs leading-5 font-bold rounded-full ${
                                                            item.status === 'Aprobada' ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400' : 
                                                            item.status === 'Rechazada' ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400' : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400'
                                                        }`}>
                                                            {item.status || 'Pendiente'}
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    )}
                </div>
                <div className="p-4 border-t dark:border-gray-700 bg-white dark:bg-gray-800 rounded-b-lg text-right transition-colors">
                    <button onClick={onClose} className="bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 px-6 py-2 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 font-medium transition-colors">Cerrar</button>
                </div>
            </div>
        </div>
    );
};

// --- MODAL DE CREACIÓN / EDICIÓN (FORMULARIO) ---
const ClientModal = ({ client, onClose, onSave, setNotification, existingRuts }) => {
    // --- ESTADOS ---
    const isPending = client?.rut === 'PENDIENTE';
    const [rut, setRut] = useState(client?.id && !isPending ? formatRut(client.id) : '');
    const [nombre, setNombre] = useState(client?.nombre || '');
    const [direccion, setDireccion] = useState(client?.direccion || '');
    const [telefono, setTelefono] = useState(client?.telefono ? formatPhone(client.telefono) : '');
    const [email, setEmail] = useState(client?.email || '');

    // Roles Específicos
    const [mainName, setMainName] = useState(client?.mainName || '');
    const [mainPhone, setMainPhone] = useState(client?.mainPhone || '');
    const [mainEmail, setMainEmail] = useState(client?.mainEmail || '');

    const [paymentName, setPaymentName] = useState(client?.paymentName || '');
    const [paymentPhone, setPaymentPhone] = useState(client?.paymentPhone || '');
    const [paymentEmail, setPaymentEmail] = useState(client?.paymentEmail || '');

    const [adminName, setAdminName] = useState(client?.adminName || '');
    const [adminPhone, setAdminPhone] = useState(client?.adminPhone || '');
    const [adminEmail, setAdminEmail] = useState(client?.adminEmail || '');

    const [conciergeName, setConciergeName] = useState(client?.conciergeName || '');
    const [conciergePhone, setConciergePhone] = useState(client?.conciergePhone || '');
    const [conciergeEmail, setConciergeEmail] = useState(client?.conciergeEmail || '');

    const [opsName, setOpsName] = useState(client?.opsName || '');
    const [opsPhone, setOpsPhone] = useState(client?.opsPhone || '');
    const [opsEmail, setOpsEmail] = useState(client?.opsEmail || '');

    const [notes, setNotes] = useState(client?.notes || '');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const isEditMode = !!client;

    // Helper para renderizar las cajas de contacto
    const ContactInputBox = ({ title, icon: Icon, name, setName, phone, setPhone, mail, setMail }) => (
        <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-500 transition-colors">
            <h4 className="font-bold text-sm text-gray-700 dark:text-gray-300 mb-3 uppercase flex items-center border-b border-gray-200 dark:border-gray-700 pb-2">
                <Icon className="w-4 h-4 mr-2 text-blue-600 dark:text-blue-400"/> {title}
            </h4>
            <div className="space-y-3">
                <div>
                    <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Nombre Completo</label>
                    <input type="text" value={name} onChange={e => setName(e.target.value)} className="w-full text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:border-blue-500 focus:ring-blue-500 px-3 py-2 outline-none" placeholder="Ej: Juan Pérez" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                    <div>
                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Teléfono</label>
                        <input type="tel" value={phone} onChange={e => setPhone(formatPhone(e.target.value))} className="w-full text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:border-blue-500 focus:ring-blue-500 px-3 py-2 outline-none" placeholder="+569..." />
                    </div>
                    <div>
                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Email</label>
                        <input type="email" value={mail} onChange={e => setMail(e.target.value)} className="w-full text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:border-blue-500 focus:ring-blue-500 px-3 py-2 outline-none" placeholder="@" />
                    </div>
                </div>
            </div>
        </div>
    );

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        const cleanRut = rut.replace(/[.-]/g, '');

        // Validación: RUT existente
        const isMigrating = isEditMode && isPending;
        if ((!isEditMode || isMigrating) && existingRuts.includes(cleanRut)) {
            setNotification({ message: 'Error: Ya existe un cliente con este RUT.', type: 'error' });
            setIsSubmitting(false);
            return;
        }

        const clientData = {
            nombre, direccion, telefono: telefono.replace(/\s/g, ''), email,
            mainName, mainPhone, mainEmail,
            paymentName, paymentPhone, paymentEmail,
            adminName, adminPhone, adminEmail,
            conciergeName, conciergePhone, conciergeEmail,
            opsName, opsPhone, opsEmail,
            notes,
            rut: cleanRut 
        };
        
        try {
            // USAMOS EL SERVICIO - Él sabe si debe crear, actualizar o migrar
            // Si es migración, pasamos el ID viejo (client.id) para que el servicio lo borre
            await clientService.saveClient(clientData, isMigrating ? client.id : null);
            
            // Actualizar estado local
            onSave({ id: cleanRut, ...clientData }, isMigrating ? client.id : null);
            
            setNotification({ 
                message: isMigrating ? 'Ficha completada y migrada a RUT oficial.' : `Cliente ${isEditMode ? 'actualizado' : 'creado'} con éxito.`, 
                type: 'success' 
            });

        } catch (error) {
            console.error(error);
            setNotification({ message: 'Error al guardar el cliente.', type: 'error' });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 backdrop-blur-sm overflow-y-auto">
            <div className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-5xl my-8 flex flex-col max-h-[90vh] shadow-2xl animate-fade-in border border-transparent dark:border-gray-700 transition-colors">
                <div className={`p-6 border-b dark:border-gray-700 ${isPending ? 'bg-yellow-600 dark:bg-yellow-700' : 'bg-blue-900 dark:bg-blue-950'} text-white rounded-t-xl transition-colors`}>
                    <h2 className="text-2xl font-bold flex items-center gap-2">
                        {isPending && <ExclamationTriangleIcon className="w-8 h-8 text-white"/>}
                        {isEditMode ? (isPending ? 'Completar Ficha Pendiente' : 'Editar Ficha Cliente') : 'Nueva Ficha Cliente'}
                    </h2>
                    <p className="text-blue-100 dark:text-blue-200 text-sm mt-1">
                        {isPending ? 'Este cliente fue creado rápidamente desde la Agenda. Por favor ingrese el RUT para oficializarlo.' : 'Complete los datos de contacto y roles específicos.'}
                    </p>
                </div>
                
                <div className="p-6 overflow-y-auto bg-white dark:bg-gray-800 transition-colors">
                    <form id="clientForm" onSubmit={handleSubmit} className="space-y-8">
                        
                        {/* 1. Datos Empresa */}
                        <div>
                            <h3 className="text-lg font-bold text-gray-800 dark:text-gray-200 mb-4 flex items-center border-b dark:border-gray-700 pb-2">
                                <BuildingOfficeIcon className="w-6 h-6 mr-2 text-blue-600 dark:text-blue-400"/> Datos de la Empresa
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">RUT Empresa *</label>
                                    <input 
                                        type="text" 
                                        value={rut} 
                                        onChange={e => setRut(formatRut(e.target.value))} 
                                        readOnly={isEditMode && !isPending} 
                                        className={`w-full px-3 py-2 outline-none rounded-md border shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500 ${isEditMode && !isPending ? 'bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400' : 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600'}`} 
                                        required 
                                        placeholder="Ej: 76.123.456-7"
                                    />
                                    {isPending && <p className="text-xs text-yellow-600 dark:text-yellow-500 mt-1 font-bold">Ingrese el RUT real para validar al cliente.</p>}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Razón Social / Nombre *</label>
                                    <input type="text" value={nombre} onChange={e => setNombre(e.target.value)} className="w-full px-3 py-2 outline-none rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500" required />
                                </div>
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Dirección Comercial</label>
                                    <input type="text" value={direccion} onChange={e => setDireccion(e.target.value)} className="w-full px-3 py-2 outline-none rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Teléfono Central</label>
                                    <input type="tel" value={telefono} onChange={e => setTelefono(formatPhone(e.target.value))} className="w-full px-3 py-2 outline-none rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email Central</label>
                                    <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full px-3 py-2 outline-none rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500" />
                                </div>
                            </div>
                        </div>

                        {/* 2. Roles de Contacto Grid */}
                        <div>
                            <h3 className="text-lg font-bold text-gray-800 dark:text-gray-200 mb-4 flex items-center border-b dark:border-gray-700 pb-2">
                                <UserGroupIcon className="w-6 h-6 mr-2 text-blue-600 dark:text-blue-400"/> Equipo de Contacto
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {/* Tarjeta Contacto Principal (Destacada) */}
                                <div className="bg-blue-50 dark:bg-blue-900/30 p-4 rounded-lg border border-blue-200 dark:border-blue-800/50 lg:col-span-3 mb-2 transition-colors">
                                    <h4 className="font-bold text-sm text-blue-800 dark:text-blue-300 mb-3 uppercase flex items-center border-b border-blue-200 dark:border-blue-800/50 pb-2">
                                        <UserIcon className="w-4 h-4 mr-2"/> Contacto Principal
                                    </h4>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div>
                                            <label className="block text-xs text-blue-600 dark:text-blue-400 mb-1">Nombre Completo</label>
                                            <input type="text" value={mainName} onChange={e => setMainName(e.target.value)} className="w-full px-3 py-2 outline-none text-sm rounded-md border border-blue-300 dark:border-blue-700 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500" />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-blue-600 dark:text-blue-400 mb-1">Teléfono</label>
                                            <input type="tel" value={mainPhone} onChange={e => setMainPhone(formatPhone(e.target.value))} className="w-full px-3 py-2 outline-none text-sm rounded-md border border-blue-300 dark:border-blue-700 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500" />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-blue-600 dark:text-blue-400 mb-1">Email</label>
                                            <input type="email" value={mainEmail} onChange={e => setMainEmail(e.target.value)} className="w-full px-3 py-2 outline-none text-sm rounded-md border border-blue-300 dark:border-blue-700 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500" />
                                        </div>
                                    </div>
                                </div>

                                <ContactInputBox title="Encargado de Pagos" icon={CurrencyDollarIcon} name={paymentName} setName={setPaymentName} phone={paymentPhone} setPhone={setPaymentPhone} mail={paymentEmail} setMail={setPaymentEmail} />
                                <ContactInputBox title="Administrador" icon={BuildingOfficeIcon} name={adminName} setName={setAdminName} phone={adminPhone} setPhone={setAdminPhone} mail={adminEmail} setMail={setAdminEmail} />
                                <ContactInputBox title="Mayordomo" icon={KeyIcon} name={conciergeName} setName={setConciergeName} phone={conciergePhone} setPhone={setConciergePhone} mail={conciergeEmail} setMail={setConciergeEmail} />
                                <ContactInputBox title="Operaciones" icon={WrenchIcon} name={opsName} setName={setOpsName} phone={opsPhone} setPhone={setOpsPhone} mail={opsEmail} setMail={setOpsEmail} />
                            </div>
                        </div>

                        {/* 3. Notas */}
                        <div>
                            <h3 className="text-lg font-bold text-gray-800 dark:text-gray-200 mb-4 flex items-center border-b dark:border-gray-700 pb-2">
                                <ChatBubbleBottomCenterTextIcon className="w-6 h-6 mr-2 text-blue-600 dark:text-blue-400"/> Notas Internas
                            </h3>
                            <textarea 
                                value={notes} 
                                onChange={e => setNotes(e.target.value)} 
                                rows="3" 
                                className="w-full px-3 py-2 outline-none rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                                placeholder="Información relevante, horarios, restricciones de acceso, etc."
                            ></textarea>
                        </div>
                    </form>
                </div>

                <div className="p-6 border-t dark:border-gray-700 bg-gray-50 dark:bg-gray-900 rounded-b-xl flex justify-end space-x-4 transition-colors">
                    <button type="button" onClick={onClose} className="bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 px-6 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 font-medium transition-colors">Cancelar</button>
                    <button type="submit" form="clientForm" disabled={isSubmitting} className="bg-blue-600 text-white px-8 py-2 rounded-lg hover:bg-blue-700 disabled:bg-blue-300 disabled:dark:bg-blue-800 font-bold shadow-lg transform hover:-translate-y-0.5 transition-all">
                        {isSubmitting ? 'Guardando...' : 'GUARDAR FICHA'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const ClientManagementPage = ({ setPage, setNotification }) => {
    const { user } = useAuth();
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingClient, setEditingClient] = useState(null);
    const [viewingClient, setViewingClient] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const fileInputRef = useRef(null); 
    
    // --- LÓGICA DE PERMISOS ---
    const userPermissions = user?.permissions || {};
    const isSuperUser = user?.role === 'superuser';
    const isAdmin = user?.role === 'admin' || isSuperUser;

    useEffect(() => {
        const fetchClients = async () => {
            try {
                // Usamos el servicio
                const data = await clientService.getAll();
                
                // ORDENAR: Poner "Pendientes" primero (Mantenemos lógica visual aquí)
                data.sort((a, b) => {
                    if (a.rut === 'PENDIENTE' && b.rut !== 'PENDIENTE') return -1;
                    if (a.rut !== 'PENDIENTE' && b.rut === 'PENDIENTE') return 1;
                    return a.nombre.localeCompare(b.nombre);
                });

                setClients(data);
            } catch (error) {
                console.error("Error fetching clients:", error);
                setNotification({ message: "Error al cargar clientes.", type: "error" });
            } finally {
                setLoading(false);
            }
        };
        fetchClients();
    }, [setNotification]);

    // --- FUNCIÓN EXPORTAR A EXCEL (CSV) ---
    // Mantenemos esta lógica en UI porque es generación de archivos del navegador
    const handleExportToExcel = () => {
        if (clients.length === 0) {
            setNotification({ message: "No hay datos para exportar.", type: "info" });
            return;
        }
        
        let csvContent = "data:text/csv;charset=utf-8,";
        csvContent += "RUT;Nombre;TelefonoCentral;EmailCentral;Direccion;" + 
                      "ContactoPpal;TelPpal;EmailPpal;" +
                      "Pagos;TelPagos;EmailPagos;" +
                      "Admin;TelAdmin;EmailAdmin;" +
                      "Mayordomo;TelMayordomo;EmailMayordomo;" +
                      "Operaciones;TelOps;EmailOps;" +
                      "Notas\n";

        clients.forEach(c => {
            const row = [
                formatRut(c.id),
                c.nombre || '',
                c.telefono || '',
                c.email || '',
                c.direccion ? c.direccion.replace(/;/g, ',') : '',
                c.mainName || '', c.mainPhone || '', c.mainEmail || '',
                c.paymentName || '', c.paymentPhone || '', c.paymentEmail || '',
                c.adminName || '', c.adminPhone || '', c.adminEmail || '',
                c.conciergeName || '', c.conciergePhone || '', c.conciergeEmail || '',
                c.opsName || '', c.opsPhone || '', c.opsEmail || '',
                c.notes ? c.notes.replace(/;/g, ',').replace(/\n/g, ' ') : ''
            ].join(";");
            csvContent += row + "\n";
        });

        const universalBOM = "\uFEFF";
        const blob = new Blob([universalBOM + csvContent.substring(csvContent.indexOf(',') + 1)], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `clientes_detalle_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setNotification({ message: "Base de clientes detallada descargada.", type: "success" });
    };

    // --- FUNCIÓN IMPORTAR DESDE CSV ---
    const handleImportClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.value = ''; 
            fileInputRef.current.click();
        }
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = async (event) => {
            try {
                const text = event.target.result;
                // Usamos el servicio para la importación masiva
                const count = await clientService.importFromCSV(text);

                if (count > 0) {
                    setNotification({ message: `Se importaron/actualizaron ${count} clientes correctamente.`, type: 'success' });
                    // Recargar lista
                    const data = await clientService.getAll();
                    setClients(data);
                } else {
                    setNotification({ message: "No se encontraron datos válidos en el archivo.", type: "info" });
                }

            } catch (error) {
                console.error("Error importando:", error);
                setNotification({ message: "Error al procesar el archivo CSV.", type: "error" });
            }
        };
        reader.readAsText(file, 'UTF-8');
    };

    const handleSaveClient = (client, oldIdToDelete = null) => {
        if (oldIdToDelete) {
             // Caso Migración: Eliminamos el viejo de la lista y agregamos el nuevo
             setClients(prev => [client, ...prev.filter(c => c.id !== oldIdToDelete)]);
        } else if (editingClient) {
            // Caso Edición normal
            setClients(clients.map(c => c.id === client.id ? client : c));
        } else {
            // Caso Creación
            setClients([client, ...clients]);
        }
        setShowModal(false);
        setEditingClient(null);
    };

    const handleDeleteClient = async (e, clientId) => {
        e.stopPropagation();
        if (!window.confirm(`¿Seguro que quieres eliminar al cliente ${clientId}? Esta acción no se puede deshacer.`)) return;
        try {
            // Usamos el servicio
            await clientService.delete(clientId);
            setClients(prev => prev.filter(client => client.id !== clientId));
            setNotification({ message: `Cliente ${clientId} eliminado.`, type: 'success' });
        } catch (error) {
            setNotification({ message: 'Error al eliminar el cliente.', type: 'error' });
        }
    };

    const filteredClients = clients.filter(client => {
        const term = searchTerm.toLowerCase();
        return (
            (client.nombre?.toLowerCase().includes(term)) ||
            (client.id?.toLowerCase().includes(term)) ||
            (client.email?.toLowerCase().includes(term)) ||
            (client.mainName?.toLowerCase().includes(term)) 
        );
    });

    const existingRuts = clients.map(c => c.id);

    return (
        <div className="p-4 md:p-6 bg-gray-50 dark:bg-gray-900 min-h-screen transition-colors duration-300">
            {showModal && (
                <ClientModal 
                    client={editingClient} 
                    onClose={() => { setShowModal(false); setEditingClient(null); }} 
                    onSave={handleSaveClient} 
                    setNotification={setNotification}
                    existingRuts={existingRuts}
                />
            )}
            {viewingClient && (
                <ClientDetailsModal 
                    client={viewingClient} 
                    onClose={() => setViewingClient(null)} 
                />
            )}

            <div className="flex items-center mb-4">
                <button onClick={() => setPage('hub')} className="text-blue-600 dark:text-blue-400 hover:underline font-semibold mr-4">&larr; Volver</button>
                <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100">Gestión de Clientes</h1>
            </div>
            <p className="mb-6 text-gray-600 dark:text-gray-400">Administra la base de datos, contactos y consulta el historial.</p>
            
            <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
                <div className="flex gap-2 w-full md:w-auto">
                    {(isAdmin || userPermissions.clients?.create) && (
                        <>
                            <button onClick={() => setShowModal(true)} className="bg-blue-600 text-white px-5 py-2 rounded-lg hover:bg-blue-700 transition whitespace-nowrap shadow-md">
                                + Nuevo Cliente
                            </button>
                            <button onClick={handleImportClick} className="bg-orange-500 text-white px-5 py-2 rounded-lg hover:bg-orange-600 transition flex items-center gap-2 whitespace-nowrap shadow-md" title="Importar desde CSV (Formato Excel)">
                                <DocumentArrowUpIcon className="w-5 h-5"/> Importar
                            </button>
                            <input type="file" ref={fileInputRef} onChange={handleFileChange} accept=".csv" className="hidden" />
                        </>
                    )}
                    <button onClick={handleExportToExcel} className="bg-green-600 text-white px-5 py-2 rounded-lg hover:bg-green-700 transition flex items-center gap-2 whitespace-nowrap shadow-md">
                        <DocumentArrowDownIcon className="w-5 h-5"/> Excel
                    </button>
                </div>
                <input 
                    type="text" 
                    placeholder="Buscar por Nombre, RUT, Contacto..." 
                    value={searchTerm} 
                    onChange={e => setSearchTerm(e.target.value)} 
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg w-full md:w-1/2 lg:w-1/3 focus:ring-2 focus:ring-blue-500 outline-none shadow-sm"
                />
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-x-auto border border-transparent dark:border-gray-700 transition-colors">
                <table className="min-w-full">
                    <thead className="bg-gray-100 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700 transition-colors">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Empresa / Cliente</th>
                            <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Contacto Principal</th>
                            <th className="px-6 py-3 text-center text-xs font-bold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Info & Historial</th>
                            <th className="px-6 py-3 text-right text-xs font-bold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700 transition-colors">
                        {loading ? (
                            <tr><td colSpan="4" className="text-center py-10 text-gray-500 dark:text-gray-400">Cargando clientes...</td></tr>
                        ) : filteredClients.length === 0 ? (
                            <tr><td colSpan="4" className="text-center py-10 text-gray-500 dark:text-gray-400">No se encontraron clientes.</td></tr>
                        ) : (
                            filteredClients.map(client => (
                                <tr key={client.id} className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${client.rut === 'PENDIENTE' ? 'bg-yellow-50 dark:bg-yellow-900/10 border-l-4 border-yellow-400 dark:border-yellow-600' : ''}`}>
                                    <td className="px-6 py-4">
                                        {client.rut === 'PENDIENTE' && (
                                            <div className="mb-1 inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-yellow-200 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-100">
                                                <ExclamationTriangleIcon className="w-3 h-3 mr-1"/> COMPLETAR FICHA
                                            </div>
                                        )}
                                        <div className="font-bold text-gray-900 dark:text-gray-100 text-base">{client.nombre}</div>
                                        <div className="text-sm text-gray-500 dark:text-gray-400">{client.rut === 'PENDIENTE' ? 'RUT Pendiente' : formatRut(client.id)}</div>
                                        <div className="text-xs text-gray-400 mt-1 truncate max-w-xs">{client.direccion}</div>
                                        {client.notes && <span className="text-[10px] bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 px-1.5 py-0.5 rounded mt-1 inline-block">Con Notas</span>}
                                    </td>

                                    <td className="px-6 py-4 whitespace-nowrap">
                                        {client.mainName ? (
                                            <div className="flex flex-col gap-1">
                                                <span className="font-semibold text-gray-800 dark:text-gray-200 text-sm flex items-center"><UserIcon className="w-3 h-3 mr-1 text-gray-400 dark:text-gray-500"/>{client.mainName}</span>
                                                {client.mainPhone && (
                                                    <a href={`tel:${client.mainPhone}`} className="flex items-center text-xs text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400">
                                                        <PhoneIcon className="w-3 h-3 mr-1"/> {formatPhone(client.mainPhone)}
                                                    </a>
                                                )}
                                                {client.mainEmail && (
                                                    <a href={`mailto:${client.mainEmail}`} className="flex items-center text-xs text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400">
                                                        <EnvelopeIcon className="w-3 h-3 mr-1"/> {client.mainEmail}
                                                    </a>
                                                )}
                                            </div>
                                        ) : (
                                            <span className="text-xs text-gray-400 dark:text-gray-500 italic">Sin contacto principal</span>
                                        )}
                                    </td>

                                    <td className="px-6 py-4 text-center">
                                        <button 
                                            onClick={() => setViewingClient(client)}
                                            className="bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 px-4 py-1.5 rounded-full text-xs font-bold flex items-center justify-center mx-auto gap-1 transition-colors shadow-sm"
                                        >
                                            <EyeIcon className="w-3 h-3"/> Ficha Completa
                                        </button>
                                    </td>

                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        {(isAdmin || userPermissions.clients?.edit) && (
                                            <button 
                                                onClick={() => { setEditingClient(client); setShowModal(true); }} 
                                                className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 mr-4 transition-colors"
                                                title="Editar"
                                            >
                                                <PencilSquareIcon className="w-5 h-5"/>
                                            </button>
                                        )}
                                        {(isAdmin || userPermissions.clients?.delete) && (
                                            <button 
                                                onClick={(e) => handleDeleteClient(e, client.id)} 
                                                className="text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-colors"
                                                title="Eliminar"
                                            >
                                                <TrashIcon className="w-5 h-5"/>
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default ClientManagementPage;